/**
 * API client configuration using Axios.
 * 
 * This module provides a configured Axios instance for making HTTP requests
 * to the backend API with automatic JWT token handling and error processing.
 */

import axios from 'axios';
import { clearTokens } from './tokenStore';

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Create Axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token and handle deduplication for GET requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add deduplication for GET requests only (safe to deduplicate)
    if (config.method?.toLowerCase() === 'get') {
      config.metadata = { shouldDeduplicate: true };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear it and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Reason: also clear IDB so Background Sync doesn't use a stale refresh
      // token after the foreground session is invalidated.
      clearTokens().catch(() => {});

      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    // 403 Forbidden - user is authenticated but not authorized
    // Let the calling component handle this (e.g., show "Admin access required")

    return Promise.reject(error);
  }
);

export default apiClient;